import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Store, FileCheck2, ArrowRight, X, Plus } from 'lucide-react';
import apiClient from '../api/client';
import { AssetMarketplace } from '../components/marketplace';
import { useToast } from '../components/ui/toastContext';
import Contracts from './Contracts';
import type { MarketplaceAsset, AssetType, AssetPayload } from '../types/marketplace';

interface AssetTemplate {
  id: string;
  type: AssetType;
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  payload: AssetPayload;
  is_builtin: boolean;
  visibility: string;
  version: number;
  /** 模板来源（builtin/plugins/manual/derived），旧版响应可能缺省 */
  source?: 'builtin' | 'plugins' | 'manual' | 'derived';
  /** 所属预置资产包标识（如 atelier-starter） */
  preset_pack?: string;
  /** 许可证标识 */
  license?: string;
}

interface Agent {
  id: string;
  name: string;
  description: string | null;
  active_profile_id: string | null;
}

const VALID_TABS: AssetType[] = ['persona', 'skill', 'tool', 'mcp', 'knowledge', 'workflow'];

function isValidAssetType(type: string): type is AssetType {
  return (VALID_TABS as string[]).includes(type);
}

export default function Marketplace() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialType: AssetType | 'all' =
    tabParam && (VALID_TABS as string[]).includes(tabParam) ? (tabParam as AssetType) : 'all';

  const [templates, setTemplates] = useState<AssetTemplate[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingAsset, setApplyingAsset] = useState<MarketplaceAsset | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [applyLoading, setApplyLoading] = useState(false);
  // 顶部区块切换：资产市场（默认）/ 能力契约（嵌入 Contracts 页内容）
  const [view, setView] = useState<'assets' | 'contracts'>('assets');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [templatesRes, agentsRes] = await Promise.all([
          apiClient.get('/asset-templates', {
            params: { visibility: 'public', include_builtin: true },
          }),
          apiClient.get('/agents'),
        ]);
        setTemplates(templatesRes.data);
        setAgents(agentsRes.data);
      } catch (e) {
        console.error('Failed to load marketplace assets:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const assets: MarketplaceAsset[] = useMemo(
    () =>
      templates
        .filter((t) => isValidAssetType(t.type))
        .map((t) => ({
          id: t.id,
          type: t.type,
          name: t.name,
          displayName: t.display_name || t.name,
          description: t.description,
          category: t.category,
          tags: t.tags,
          // 后端响应若未携带 source 字段，则按 is_builtin 回退推导
          source: t.source || (t.is_builtin ? 'builtin' : 'manual'),
          presetPack: t.preset_pack,
          license: t.license,
          payload: t.payload,
          // 安全扫描摘要（B11）：入库时写入 payload.security_scan，此处提升为顶层字段
          securityScan: t.payload.security_scan as MarketplaceAsset['securityScan'],
          metadata: {
            visibility: t.visibility,
            version: t.version,
            preset_pack: t.preset_pack,
            license: t.license,
          },
        })),
    [templates]
  );

  const handleApply = (asset: MarketplaceAsset) => {
    setApplyingAsset(asset);
    setSelectedAgentId('');
  };

  const closeApplyModal = () => {
    setApplyingAsset(null);
    setSelectedAgentId('');
  };

  const confirmApply = async () => {
    if (!applyingAsset || !selectedAgentId) return;
    const agent = agents.find((a) => a.id === selectedAgentId);
    if (!agent) {
      toast.error('未找到所选 Agent');
      return;
    }
    if (!agent.active_profile_id) {
      toast.error('该 Agent 没有激活的 Profile，请先进入配置中心创建 Profile');
      return;
    }
    setApplyLoading(true);
    try {
      await apiClient.post(`/asset-templates/${applyingAsset.id}/instantiate`, {
        target_profile_id: agent.active_profile_id,
      });
      toast.success(`已将 "${applyingAsset.displayName}" 应用到 ${agent.name}`);
      closeApplyModal();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '应用失败';
      toast.error(`应用失败：${msg}`);
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ margin: '0 0 8px' }}>
            <Store size={24} style={{ verticalAlign: 'middle', marginRight: 10 }} />
            能力市场
          </h1>
          <p className="text-muted">浏览可复用的人设、技能、工具、工作流、知识等资产模板，应用到 Agent 配置中心。</p>
        </div>
        <button className="primary" onClick={() => navigate('/')}>
          去 Agent 列表 <ArrowRight size={14} />
        </button>
      </div>

      <div className="segmented-control mb-4" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`segment ${view === 'assets' ? 'active' : ''}`}
          onClick={() => setView('assets')}
        >
          <Store size={14} className="inline mr-1" /> 资产市场
        </button>
        <button
          type="button"
          className={`segment ${view === 'contracts' ? 'active' : ''}`}
          onClick={() => setView('contracts')}
        >
          <FileCheck2 size={14} className="inline mr-1" /> 能力契约
        </button>
      </div>

      {view === 'assets' ? (
        <>
          {loading ? (
            <div className="loading">
              <div className="spinner" />
            </div>
          ) : (
            <AssetMarketplace
              assets={assets}
              title="全部可复用资产"
              initialType={initialType}
              hideTypeFilter={false}
              onApply={handleApply}
            />
          )}

          {applyingAsset && (
            <>
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.6)',
                  zIndex: 200,
                }}
                onClick={closeApplyModal}
              />
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  maxWidth: 420,
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-xl)',
                  zIndex: 210,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'var(--shadow-lg)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--border-default)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: 16 }}>应用到 Agent</h3>
                  <button className="secondary" style={{ padding: '6px 8px' }} onClick={closeApplyModal}>
                    <X size={18} />
                  </button>
                </div>

                <div style={{ padding: '20px 24px' }}>
                  <p className="text-muted" style={{ margin: '0 0 16px' }}>
                    选择要将 <strong>"{applyingAsset.displayName}"</strong> 应用到的 Agent：
                  </p>
                  {agents.length === 0 ? (
                    <div className="empty" style={{ padding: 24 }}>
                      <p>暂无 Agent，请先创建 Agent</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                      {agents.map((agent) => (
                        <label
                          key={agent.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: 12,
                            borderRadius: 'var(--radius-md)',
                            border: `1px solid ${selectedAgentId === agent.id ? 'var(--color-primary)' : 'var(--border-default)'}`,
                            background: selectedAgentId === agent.id ? 'rgba(88, 166, 255, 0.08)' : 'var(--bg-primary)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="target-agent"
                            value={agent.id}
                            checked={selectedAgentId === agent.id}
                            onChange={() => setSelectedAgentId(agent.id)}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: 14 }}>{agent.name}</div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {agent.active_profile_id ? '已激活 Profile' : '未激活 Profile'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '1px solid var(--border-default)',
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'flex-end',
                  }}
                >
                  <button className="secondary" onClick={closeApplyModal} disabled={applyLoading}>
                    取消
                  </button>
                  <button
                    className="primary"
                    onClick={confirmApply}
                    disabled={!selectedAgentId || applyLoading}
                  >
                    {applyLoading ? (
                      '应用中...'
                    ) : (
                      <>
                        <Plus size={14} /> 确认应用
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <Contracts />
      )}
    </div>
  );
}
